<template>
  <div class="massage-page">
    <main class="prepare-content">
      <section class="prepare-title">
        <h1>{{ massagePlanTitle }}前准备</h1>
        <p>请确认宝宝状态、操作环境和物品准备都适合后再进入教程。</p>
      </section>

      <section class="unsuitable-card">
        <div class="unsuitable-title">
          <span>状态确认</span>
          <strong>如有以下情况，本次不适合做推拿</strong>
        </div>
        <van-checkbox-group v-model="unsuitableReasons" checked-color="#D86C4A" class="reason-grid">
          <button
            v-for="item in unsuitableOptions"
            :key="item.value"
            type="button"
            class="reason-option"
            :class="{ 'is-checked': unsuitableReasons.indexOf(item.value) !== -1 }"
            @click="toggleReason(item.value)"
          >
            <van-checkbox :name="item.value" shape="square" @click.stop />
            <span>{{ item.label }}</span>
          </button>
        </van-checkbox-group>
        <van-field
          v-if="unsuitableReasons.indexOf('other') !== -1"
          v-model.trim="unsuitableOtherDesc"
          class="other-reason-field"
          type="textarea"
          rows="2"
          autosize
          placeholder="请填写其他不适合做的原因"
        />
      </section>

      <section v-for="section in sections" :key="section.title" class="prepare-card">
        <div class="card-title">
          <span>{{ section.index }}</span>
          <h2>{{ section.title }}</h2>
        </div>
        <ul>
          <li v-for="item in section.items" :key="item">{{ item }}</li>
        </ul>
      </section>
    </main>

    <div class="bottom-action">
      <van-button plain block color="#D86C4A" :disabled="submitting" @click="confirmUnsuitable">不适合做</van-button>
      <van-button block color="#2F8F6B" :loading="submitting" @click="confirmPrepare">以上都准备好了</van-button>
    </div>
  </div>
</template>

<script>
import { resolveTcmStudyToken, submitMassagePrepare, submitMassageUnsuitable } from '@/api/tcmStudy';

const PLAN_1_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html';
const PLAN_2_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-07-03/36cEcYvh1E6.html';

export default {
    name: 'TcmMassagePrepare',
    data () {
        return {
            token: '',
            submitting: false,
            unsuitableReasons: [],
            unsuitableOtherDesc: '',
            unsuitableOptions: [
                { label: '生病中', value: 'sick' },
                { label: '皮肤泛红/麻疹', value: 'skin_rash' },
                { label: '肠胃不适', value: 'gastrointestinal' },
                { label: '其他', value: 'other' }
            ],
            sections: [
                {
                    index: '01',
                    title: '操作人准备',
                    items: ['操作前洗净双手、修剪指甲。', '不佩戴首饰。', '保持手部温暖。']
                },
                {
                    index: '02',
                    title: '宝宝准备',
                    items: [
                        '确认宝宝清醒、情绪稳定，无发热、腹泻、皮肤破损等不适。',
                        '避免在宝宝空腹或饱腹时操作，避开餐前 1 小时内或餐后 2 小时内。',
                        '给宝宝更换干净尿布，保持宝宝身体舒适。'
                    ]
                },
                {
                    index: '03',
                    title: '材料准备',
                    items: ['穴位定位贴 / 推拿尺。', '婴儿专用润肤霜，皮肤干燥时使用。', '时钟，用于把控操作时长。']
                },
                {
                    index: '04',
                    title: '环境准备',
                    items: ['操作环境安静、温暖，室温建议 24-26℃。', '光线充足。', '在柔软的床上或操作台上进行，铺好干净的隔尿垫。']
                },
                {
                    index: '05',
                    title: '异常提醒',
                    items: ['若宝宝在操作后出现皮肤发红、腹胀、呕吐等异常情况，监护人需立即暂停操作。', '及时记录异常表现，并及时上报照护师。']
                }
            ]
        };
    },
    mounted () {
        this.token = resolveTcmStudyToken(this.$route);
    },
    computed: {
        massagePlanTitle () {
            return this.$route.query.massage_plan_title || '小儿推拿方案1';
        },
        massagePlanNo () {
            return Number(this.$route.query.massage_plan_no) === 2 ? 2 : 1;
        },
        tutorialUrl () {
            if (this.massagePlanNo === 2) {
                return PLAN_2_TUTORIAL_URL;
            }
            return this.$route.query.tutorial_url || PLAN_1_TUTORIAL_URL;
        }
    },
    methods: {
        returnToTaskPage (replace = false) {
            const query = {
                ...this.$route.query,
                date: this.$route.query.record_date || this.$route.query.date
            };
            delete query.return_path;
            const target = {
                path: this.$route.query.return_path || '/tcm-study/home',
                query
            };
            if (replace) {
                this.$router.replace(target);
            } else {
                this.$router.push(target);
            }
        },
        queryPayload () {
            return {
                baby_id: this.$route.query.baby_id,
                subject_id: this.$route.query.subject_id || undefined,
                record_date: this.$route.query.record_date,
                task_key: this.$route.query.task_key || 'massage',
                record_id: this.$route.query.record_id || undefined
            };
        },
        toggleReason (value) {
            const index = this.unsuitableReasons.indexOf(value);
            if (index === -1) {
                this.unsuitableReasons.push(value);
            } else {
                this.unsuitableReasons.splice(index, 1);
            }
        },
        async confirmUnsuitable () {
            if (!this.unsuitableReasons.length) {
                this.$toast('请选择不适合做的原因');
                return;
            }
            if (this.unsuitableReasons.indexOf('other') !== -1 && this.unsuitableOtherDesc.length < 2) {
                this.$toast('请填写其他原因');
                return;
            }
            if (this.submitting) {
                return;
            }
            this.submitting = true;
            const labels = this.unsuitableOptions
                .filter(item => this.unsuitableReasons.indexOf(item.value) !== -1)
                .map(item => item.label)
                .join('、');
            try {
                await submitMassageUnsuitable(this.token, {
                    ...this.queryPayload(),
                    reason_types: this.unsuitableReasons,
                    other_desc: this.unsuitableOtherDesc
                });
                await this.$dialog.alert({
                    title: '已记录不适合做',
                    message: `原因：${labels}\n请暂停本次推拿，必要时咨询照护师。`,
                    confirmButtonText: '我知道了'
                });
                this.returnToTaskPage(true);
            } catch (error) {
                this.$toast(error.message || '提交失败');
            } finally {
                this.submitting = false;
            }
        },
        async confirmPrepare () {
            if (this.submitting) {
                return;
            }
            if (this.unsuitableReasons.length) {
                this.$toast('已选择不适合做，请先取消原因后再继续');
                return;
            }
            this.submitting = true;
            try {
                const result = await submitMassagePrepare(this.token, this.queryPayload());
                this.$router.replace({
                    path: '/tcm-study/massage/tutorial',
                    query: {
                        ...this.$route.query,
                        task_record_id: result.task_record_id || result.id,
                        tutorial_url: this.massagePlanNo === 2 ? PLAN_2_TUTORIAL_URL : (result.tutorial_url || this.tutorialUrl),
                        return_path: this.$route.query.return_path || '/tcm-study/home'
                    }
                });
            } catch (error) {
                this.$toast(error.message || '提交失败');
            } finally {
                this.submitting = false;
            }
        }
    }
};
</script>

<style lang="sass" scoped>
.massage-page
    --native-header-safe-top: calc(env(safe-area-inset-top) + 48px)
    --home-indicator-safe-bottom: max(env(safe-area-inset-bottom), 22px)
    min-height: 100vh
    background: #f7f8fa
    color: #1f2329
    padding-top: var(--native-header-safe-top)
    box-sizing: border-box

.prepare-content
    padding: 14px 14px calc(138px + var(--home-indicator-safe-bottom))

.prepare-title h1
    margin: 0
    font-size: 18px
    line-height: 26px
    font-weight: 700

.prepare-title p
    margin: 4px 0 12px
    color: #646a73
    font-size: 13px
    line-height: 20px

.unsuitable-card
    margin-bottom: 10px
    padding: 14px
    background: #fff8f5
    border-radius: 8px
    border: 1px solid #f4d7cc
    box-sizing: border-box

.unsuitable-title
    display: flex
    flex-direction: column
    gap: 4px
    margin-bottom: 10px

.unsuitable-title span
    color: #d86c4a
    font-size: 12px
    line-height: 17px
    font-weight: 600

.unsuitable-title strong
    color: #1f2329
    font-size: 15px
    line-height: 22px
    font-weight: 700

.reason-grid
    display: grid
    grid-template-columns: repeat(2, minmax(0, 1fr))
    gap: 8px

.reason-option
    min-width: 0
    min-height: 40px
    display: flex
    align-items: center
    gap: 7px
    padding: 8px
    border: 1px solid #f1d7cd
    border-radius: 8px
    background: #ffffff
    color: #4e5969
    font-size: 13px
    line-height: 18px
    text-align: left
    box-sizing: border-box

.reason-option.is-checked
    border-color: #d86c4a
    background: #fff0ea
    color: #a94227

.reason-option span
    min-width: 0
    flex: 1

.other-reason-field
    margin-top: 10px
    border-radius: 8px
    overflow: hidden

.prepare-card
    margin-bottom: 10px
    padding: 14px
    background: #ffffff
    border-radius: 8px
    border: 1px solid #eef0f3
    box-sizing: border-box
    box-shadow: 0 2px 10px rgba(31, 35, 41, .03)

.card-title
    display: flex
    align-items: center
    gap: 8px
    margin-bottom: 8px

.card-title span
    width: 28px
    height: 20px
    display: inline-flex
    align-items: center
    justify-content: center
    border-radius: 10px
    background: #eaf6f1
    color: #2f8f6b
    font-size: 11px
    font-weight: 700

.card-title h2
    margin: 0
    color: #1f2329
    font-size: 15px
    line-height: 22px
    font-weight: 600

.prepare-card ul
    margin: 0
    padding-left: 17px

.prepare-card li
    color: #646a73
    font-size: 13px
    line-height: 20px
    margin-top: 4px

.prepare-card li:first-child
    margin-top: 0

.bottom-action
    position: fixed
    left: 0
    right: 0
    bottom: 0
    display: grid
    grid-template-columns: 112px minmax(0, 1fr)
    gap: 10px
    padding: 10px 14px calc(10px + var(--home-indicator-safe-bottom))
    background: #ffffff
    box-shadow: 0 -2px 8px rgba(0, 0, 0, .04)
    box-sizing: border-box

::v-deep .bottom-action .van-button
    height: 44px
    border-radius: 8px
    font-size: 15px
    font-weight: 600

::v-deep .reason-option .van-checkbox__icon
    font-size: 17px
</style>
